<template>
  <v-container>
    <v-row>
      <!-- Autocompletados dinámicos -->
      <v-col
        v-for="(field, index) in filterFields"
        :key="'field-' + index"
        :class="field.class"
        cols="12"
        sm="6"
      >
        <v-autocomplete
          v-model="formData[field.model]"
          :items="field.items"
          :label="field.label"
          item-text="text"
          item-value="value"
          dense
          outlined
          clearable
        />
      </v-col>

      <!-- Selectores de fecha -->
      <v-col
        v-for="dateField in dateFields"
        :key="dateField.model"
        class="d-flex"
        cols="12"
        sm="6"
      >
        <v-menu
          v-model="dateField.menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formData[dateField.model]"
              :label="dateField.label"
              persistent-hint
              prepend-icon="calendar_month"
              readonly
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="formData[dateField.model]"
            no-title
            @input="dateField.menu = false"
          />
        </v-menu>
      </v-col>

      <!-- Botón de acción -->
      <v-col cols="4">
        <div class="text-center">
          <v-btn :loading="loading" color="primary" @click="generateReport">
            Generar Reporte
          </v-btn>
        </div>
      </v-col>
      <v-col cols="4" class="text-center">
        <v-btn :loading="loading" color="success" @click="exportToExcel">
          <v-icon left>mdi-microsoft-excel</v-icon>
          Exportar a Excel
        </v-btn>
      </v-col>
      <v-col cols="4" class="text-center">
        <v-btn :loading="loading" color="error" @click="exportToPDF">
          <v-icon left>mdi-file-pdf</v-icon>
          Exportar a PDF
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabla de resultados con motivo permanente -->
    <v-data-table
      :headers="headers"
      :items="processedReport"
      :search="search"
      :items-per-page="10"
      :footer-props="{
        'items-per-page-options': [10, 20, 50, 100, -1],
        'items-per-page-text': 'Registros por página',
        'show-current-page': true,
        'show-first-last-page': true,
        'page-text': '{0}-{1} de {2}',
      }"
      class="elevation-1"
      :loading="loading"
      loading-text="Cargando reporte..."
      style="background: rgba(255, 255, 255, 0.8)"
    >
      <template v-slot:body="{ items }">
        <tbody>
          <template v-for="(item, index) in items">
            <!-- Fila principal -->
            <tr :key="`row-${item.id || index}`">
              <td>{{ index + 1 }}</td>
              <td>{{ item.cite }}</td>
              <td>
                <div>
                  <div class="text-caption">{{ item.userName }}</div>
                  <strong>{{ item.userPosition }}</strong>
                </div>
              </td>
              <td align="center">{{ item.unitDescription }}</td>
              <td align="center">
                <div>
                  <div class="text-caption">{{ item.location }}</div>
                  <div class="text-caption">{{ item.transportName }}</div>
                </div>
              </td>
              <td align="center">
                <div class="date-time-container">
                  <div class="text-caption">
                    {{ formatDate(item.startDate).split("\n")[0] }}
                  </div>
                  <div class="text-caption">
                    {{ formatDate(item.startDate).split("\n")[1] }}
                  </div>
                  <v-divider class="my-1"></v-divider>
                  <div class="text-caption">
                    {{ formatDate(item.endDate).split("\n")[0] }}
                  </div>
                  <div class="text-caption">
                    {{ formatDate(item.endDate).split("\n")[1] }}
                  </div>
                </div>
              </td>
              <td align="center">
                <div>
                  <div class="text-caption">{{ item.tripType }}</div>
                  <div class="text-caption">{{ item.resolution }}</div>
                </div>
              </td>
              <td align="center">{{ item.days }}</td>
              <td align="center">
                <div class="text-caption">
                  {{ item.totalAllowance }}
                </div>
              </td>
              <td align="center">
                <div class="text-caption">
                  {{ item.totalTickets }}
                </div>
              </td>
            </tr>

            <!-- Fila de motivo -->
            <tr :key="`motive-${item.id || index}`" class="motive-row">
              <td colspan="100%" class="pa-3">
                <div class="motive-container">
                  <div class="motive-content">
                    <strong>Motivo:</strong>
                    {{ item.motive || "No especificado" }}
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import axios from "axios";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  name: "Reporte",
  data: () => ({
    formData: {
      id_oficina: "",
      iddepto: "",
      resolucion: "",
      trans: "",
      iduser: "",
      unidad: "",
      tv: "",
      estado: "",
      fecha1: format(new Date(new Date().getFullYear(), 0, 1), "yyyy-MM-dd"),
      fecha2: format(new Date(), "yyyy-MM-dd"),
    },
    loading: false,
    search: "",
    report: [],
    officeItems: [{ text: "TODOS", value: "" }],
    unitItems: [{ text: "TODOS", value: "" }],
    departmentItems: [{ text: "TODOS", value: "" }],
    resolutionItems: [{ text: "TODOS", value: "" }],
    transportItems: [{ text: "TODOS", value: "" }],
    userItems: [{ text: "TODOS", value: "" }],
    tripTypeItems: [{ text: "TODOS", value: "" }],
    statusItems: [{ text: "TODOS", value: "" }],
  }),

  computed: {
    filterFields() {
      return [
        {
          model: "id_oficina",
          label: "Programa",
          items: this.unitItems,
          class: "d-flex",
        },
        {
          model: "unidad",
          label: "Oficina",
          items: this.officeItems,
          class: "flex-grow-0 flex-shrink-0",
        },
        {
          model: "iddepto",
          label: "Departamento",
          items: this.departmentItems,
          class: "flex-grow-0 flex-shrink-0",
        },
        {
          model: "resolucion",
          label: "Resolución",
          items: this.resolutionItems,
          class: "d-flex",
        },
        {
          model: "trans",
          label: "Transporte",
          items: this.transportItems,
          class: "d-flex",
        },
        {
          model: "iduser",
          label: "Usuario",
          items: this.userItems,
          class: "d-flex",
        },
        {
          model: "tv",
          label: "Tipo de viaje",
          items: this.tripTypeItems,
          class: "d-flex",
        },
        {
          model: "estado",
          label: "Estado",
          items: this.statusItems,
          class: "d-flex",
        },
      ];
    },

    dateFields() {
      return [
        { model: "fecha1", label: "Fecha Inicio", menu: false },
        { model: "fecha2", label: "Fecha Fin", menu: false },
      ];
    },

    processedReport() {
      return this.report.map((item) => ({
        ...item,
        id: item.id || Math.random().toString(36).substr(2, 9),
        userName: item.comisionado_comisionado?.usuario_usuario?.nombre || "",
        userPosition: item.comisionado_comisionado?.cargo || "",
        unitDescription: item.idunidad_unidad?.descripcion || "",
        startDate: item.fecha1,
        endDate: item.fecha2,
        location: item.lugar || "",
        transportName: item.transporte_transporte?.nombre || "",
        tripType: this.getTripType(item),
        resolution: item.resolucion_resolucion?.descripcion || "",
        totalAllowance: item.total_viatico || 0,
        totalTickets: this.calculateTotalTickets(item.pasajes),
        days: item.dias || 0,
        motive: item.motivo,
        cite: item.cite || "",
      }));
    },

    headers() {
      return [
        { text: "N°", value: "gestion", sortable: true, width: "50px" },
        {
          text: "AUTORIZACIÓN",
          value: "cite",
          sortable: false,
          width: "120px",
        },
        {
          text: "NOMBRE/CARGO",
          value: "nombre-cargo",
          sortable: false,
          width: "200px",
        },
        {
          text: "PROGRAMA",
          value: "programa",
          sortable: false,
          align: "center",
          width: "150px",
        },
        {
          text: "LUGAR/TRANSPORTE",
          value: "lugar-transporte",
          sortable: false,
          align: "center",
          width: "150px",
        },
        {
          text: "IDA/RETORNO",
          value: "fechas",
          sortable: false,
          align: "center",
          width: "150px",
        },
        {
          text: "TIPO/RESOLUCIÓN",
          value: "tipo-resolucion",
          sortable: false,
          align: "center",
          width: "150px",
        },
        {
          text: "DÍAS",
          value: "dias",
          sortable: false,
          align: "center",
          width: "80px",
        },
        {
          text: "VIÁTICOS",
          value: "viatico",
          sortable: false,
          align: "center",
          width: "120px",
        },
        {
          text: "PASAJE",
          value: "pasaje",
          sortable: false,
          align: "center",
          width: "120px",
        },
      ];
    },
  },

  created() {
    this.initializeData();
  },

  methods: {
    async initializeData() {
      try {
        const header = { Token: this.$store.state.token };
        const conf = { headers: header };

        const endpoints = [
          {
            url: "oficina/listOffice",
            property: "officeItems",
            textKey: "nombre",
          },
          {
            url: "departamento/list",
            property: "departmentItems",
            textKey: "departamento",
          },
          {
            url: "transporte/list",
            property: "transportItems",
            textKey: "nombre",
          },
          { url: "unidad/list", property: "unitItems", textKey: "descripcion" },
          {
            url: "resolucion/list",
            property: "resolutionItems",
            textKey: "descripcion",
          },
          { url: "estado/list", property: "statusItems", textKey: "nombre" },
        ];

        await Promise.all(
          endpoints.map(async ({ url, property, textKey }) => {
            const response = await axios.get(url, conf);
            this[property] = [
              { text: "TODOS", value: "" },
              ...response.data.map((item) => ({
                text: item[textKey],
                value: item.id,
              })),
            ];
          })
        );

        const [nacional, internacional] = await Promise.all([
          axios.get("region_nacional/list", conf),
          axios.get("region_internacional/list", conf),
        ]);

        this.tripTypeItems = [
          { text: "TODOS", value: "" },
          ...nacional.data.map((item) => ({
            text: item.nombre,
            value: item.id,
          })),
          ...internacional.data.map((item) => ({
            text: item.nombre,
            value: item.id + 3,
          })),
        ];
      } catch (error) {
        console.error("Error inicializando datos:", error);
      }
    },

    async generateReport() {
      this.loading = true;
      try {
        const response = await axios.post("viaje/report", this.formData, {
          headers: { Token: this.$store.state.token },
        });
        this.report = response.data;
      } catch (error) {
        console.error("Error generando reporte:", error);
        this.$toast.error("Error al generar el reporte");
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A\nN/A";

      try {
        const date = parseISO(dateString);
        if (isNaN(date.getTime())) return "N/A\nN/A";

        // Formatear fecha y hora por separado
        const datePart = format(date, "dd/MM/yyyy", { locale: es });
        const timePart = format(date, "HH:mm", { locale: es });

        return `${datePart}\n${timePart}`;
      } catch (error) {
        console.error("Error formateando fecha:", error);
        return "N/A\nN/A";
      }
    },

    calculateTotalTickets(tickets = []) {
      return tickets.reduce((sum, ticket) => sum + (ticket.total || 0), 0);
    },

    getTripType(item) {
      if (item.viaje_nacionals?.length > 0) {
        return (
          item.viaje_nacionals[0].region_nacional_region_nacional?.nombre || ""
        );
      }
      if (item.viaje_internacionals?.length > 0) {
        return (
          item.viaje_internacionals[0].region_internacional_region_internacional
            ?.nombre || ""
        );
      }
      return "";
    },
    async exportToExcel() {
      try {
        this.loading = true;
        // Preparar datos
        const excelData = this.processedReport.map((item) => ({
          "N°": item.id,
          Autorización: item.cite,
          Nombre: item.userName,
          Cargo: item.userPosition,
          Programa: item.unitDescription,
          Lugar: item.location,
          Transporte: item.transportName,
          "Fecha Ida": this.formatDateExcel(item.startDate),
          "Fecha Retorno": this.formatDateExcel(item.endDate),
          "Tipo Viaje": item.tripType,
          Resolución: item.resolution,
          Días: item.days,
          Viáticos: item.totalAllowance,
          Pasajes: item.totalTickets,
          Motivo: item.motive,
        }));

        // Crear libro de Excel
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Reporte de Viajes");

        // Descargar archivo
        XLSX.writeFile(
          workbook,
          `Reporte_Viajes_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        this.loading = false;
        //this.$toast.success("Exportación a Excel completada");
      } catch (error) {
        console.error("Error al exportar a Excel:", error);
        this.loading = false;
        //this.$toast.error("Error al exportar a Excel");
      }
    },

    async exportToPDF() {
      try {
        this.loading = true;
        // Configurar PDF
        const doc = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "letter",
        });

        // Tamaño de página
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Cargar imagen de fondo
        const bgImage = await this.loadImageFromAssets("membreteLat.png");

        // Configuración de estilos
        const styles = {
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [240, 240, 240],
          },
          margin: { top: 30, left: 15, right: 15 },
          styles: {
            fontSize: 8,
            cellPadding: 4,
            overflow: "linebreak",
            cellWidth: "wrap",
          },
          startY: 25,
          // Mostrar el motivo como fila expandida
          didDrawCell: (data) => {
            if (
              data.section === "body" &&
              data.column.dataKey === "userName" &&
              data.row.raw.motivo
            ) {
              doc.setFontSize(7);
              doc.setTextColor(80);
              doc.text(
                `Motivo: ${data.row.raw.motivo}`,
                data.cell.x + 2,
                data.cell.y + data.cell.height - 2,
                { maxWidth: data.cell.width - 4 }
              );
            }
          },
        };

        const columns = [
          { title: "N°", dataKey: "index", cellWidth: 8 },
          { title: "AUTORIZACIÓN", dataKey: "cite", cellWidth: 20 },
          { title: "NOMBRE/CARGO", dataKey: "userName", cellWidth: 20 },
          //{ title: "CARGO", dataKey: "userPosition", cellWidth: 25 },
          { title: "PROGRAMA", dataKey: "unitDescription", cellWidth: 20 },
          { title: "LUGAR/TRANSPORTE", dataKey: "location", cellWidth: 20 },
          //{ title: "TRANSPORTE", dataKey: "transportName", cellWidth: 15 },
          { title: "IDA/RETORNO", dataKey: "startDate", cellWidth: 20 },
          //{ title: "FECHA RETORNO", dataKey: "endDate", cellWidth: 20 },
          { title: "DÍAS", dataKey: "days", cellWidth: 10 },
          { title: "VIÁTICOS", dataKey: "totalAllowance", cellWidth: 15 },
          { title: "PASAJES", dataKey: "totalTickets", cellWidth: 15 },
        ];

        // Preparar datos
        const rows = this.processedReport.map((item, index) => ({
          index: index + 1,
          cite: item.cite || "N/A",
          userName: `${item.userName}\n${item.userPosition}` || "N/A",
          //userPosition: item.userPosition || "N/A",
          unitDescription: item.unitDescription || "N/A",
          location: `${item.location}\n${item.transportName}` || "N/A",
          //transportName: item.transportName || "N/A",
          startDate: `${this.formatDatePDF(
            item.startDate
          )}\n${this.formatDatePDF(item.endDate)}`,
          //endDate: this.formatDatePDF(item.endDate),
          days: item.days || "N/A",
          totalAllowance: item.totalAllowance || "N/A",
          totalTickets: item.totalTickets || "N/A",
          motivo: item.motive || "Sin motivo especificado",
        }));
        // Función para agregar fondo y número de página
        const addPageElements = (pageNumber) => {
          if (bgImage) {
            doc.addImage({
              imageData: bgImage,
              x: 0,
              y: 0,
              width: pageWidth,
              height: pageHeight,
              format: "PNG",
            });
          }

          doc.setFontSize(10);
          doc.setTextColor(100);
          doc.text(`Página ${pageNumber}`, pageWidth - 20, pageHeight - 10);
        };

        // Agregar elementos a la primera página
        addPageElements(1);

        // Generar la tabla
        doc.autoTable({
          ...styles,
          columns,
          body: rows,
          didDrawPage: (data) => {
            // Limpiar la página antes de dibujar
            doc.setDrawColor(255, 255, 255);
            doc.setFillColor(255, 255, 255);
            doc.rect(0, 0, pageWidth, pageHeight, "F");

            // Agregar fondo y número a páginas subsiguientes
            if (data.pageNumber > 1) {
              addPageElements(data.pageNumber);
            }

            // Restaurar posición Y para continuar
            data.cursor.y = 25;
          },
        });

        // Guardar el PDF
        doc.save(
          `Reporte_Viajes_${new Date().toISOString().split("T")[0]}.pdf`
        );
        this.loading = false;
        //this.$toast.success('PDF generado correctamente');
      } catch (error) {
        console.error("Error al generar PDF:", error);
        this.loading = false;
        //this.$toast.error('Error al generar PDF: ' + error.message);
      }
    },

    // Función para acortar texto si es muy largo
    shortenText(text, maxLength) {
      if (!text) return "N/A";
      return text.length > maxLength
        ? text.substring(0, maxLength - 3) + "..."
        : text;
    },

    // Función para formatear fechas
    formatDatePDF(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return isNaN(date.getTime())
        ? dateString
        : date.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
    },
    async loadImageFromAssets(filename) {
      return new Promise((resolve) => {
        const image = require(`@/assets/${filename}`);
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = image;
      });
    },
    formatDateExcel(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? dateString : date.toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.date-time-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.date-time-container .text-caption {
  line-height: 1.2;
  white-space: nowrap;
}
.text-caption {
  font-size: 0.8rem;
  line-height: 1.2;
}

.v-data-table {
  margin-top: 20px;
}

.motive-row {
  background-color: #f9f9f9;
  border-bottom: 2px solid #e0e0e0;
}

.motive-container {
  padding: 2px;
}

.motive-content {
  margin-top: 2px;
  padding: 10px;
  background-color: white;
  border: 1px solid #eee;
  border-radius: 8px;
  white-space: pre-line;
}

.v-data-table >>> th {
  font-weight: bold !important;
  background-color: #f5f5f5;
}

.v-data-table >>> td {
  vertical-align: middle !important;
}
.v-data-table {
  background-color: white !important;
}

.v-btn--export {
  margin: 8px;
}
</style>
